import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Application } from "@microsoft/microsoft-graph-types";
import TokenIcon from '@mui/icons-material/Token';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * Props: 
 *  - modifiedApp: Current Application object in local state
 */
interface OptionalClaimsProps {
  modifiedApp: Application;
}

/** Example known claims for user reference. Adjust as you wish. */
const KNOWN_OPTIONAL_CLAIMS = [
  {
    name: "email",
    recommendedPermissions: ["email"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#email",
  },
  {
    name: "family_name",
    recommendedPermissions: ["profile"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#family_name",
  },
  {
    name: "given_name",
    recommendedPermissions: ["profile"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#given_name",
  },
  {
    name: "upn",
    recommendedPermissions: ["User.Read"],
    docsLink:
      "https://learn.microsoft.com/azure/active-directory/develop/active-directory-optional-claims#upn",
  },
];

/** The OptionalClaims component */
const OptionalClaims: React.FC<OptionalClaimsProps> = ({
  modifiedApp,
}) => {  // New state variable for toggling explanation
  const [showClaimsExplanation, setShowClaimsExplanation] = useState<boolean>(false);
  const [optionalClaimsExpanded, setOptionalClaimsExpanded] = useState<boolean>(true);

  // Handlers for parent accordions
  const handleOptionalClaimsChange = (event: React.SyntheticEvent, expanded: boolean) => {
    setOptionalClaimsExpanded(expanded);
  };

  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        {/* Main Title for the entire Token Configuration section */}
        {/* <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
            fontSize: "1.3rem",
            fontWeight: 600,
            color: "primary.main",
            letterSpacing: "0.01em",
            mb: 3
          }}
        >
          Token Configuration
        </Typography> */}        {/* OPTIONAL CLAIMS SECTION - Updated with better visual structure */}
        <Accordion 
          expanded={optionalClaimsExpanded} 
          onChange={handleOptionalClaimsChange}
          sx={{
            border: '1px solid #e0e7ff', 
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            mb: 3
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #e8eaf6 0%, #f3e5f5 100%)',
              borderBottom: optionalClaimsExpanded ? '1px solid #e0e7ff' : 'none',
              minHeight: '64px'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              width: '100%',
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}>
                <TokenIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#1a237e",
                  letterSpacing: "0.02em",
                  flexGrow: 1
                }}
              >
                Optional Claims
              </Typography>
              <Chip 
                size="small"
                label={`${(modifiedApp.optionalClaims?.accessToken?.length || 0) + 
                        (modifiedApp.optionalClaims?.idToken?.length || 0) + 
                        (modifiedApp.optionalClaims?.saml2Token?.length || 0)} claims`}
                sx={{ 
                  mr: 1,
                  backgroundColor: '#e3f2fd',
                  color: '#1565c0',
                  fontWeight: 500,
                  border: '1px solid #bbdefb'
                }}
              />
              <Tooltip title="Configure in Entra Portal">
                <IconButton
                  size="small"
                  component="a"
                    href={`https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/TokenConfiguration/appId/${modifiedApp.appId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={{ ml: 1, color: '#5c6bc0' }}
                >
                  <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </AccordionSummary>          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ backgroundColor: '#fafbff' }}>
              {/* Enhanced empty state */}
              {(!modifiedApp.optionalClaims?.accessToken?.length && 
                !modifiedApp.optionalClaims?.idToken?.length && 
                !modifiedApp.optionalClaims?.saml2Token?.length) ? (
                <Box sx={{ 
                  p: 4, 
                  m: 2,
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '12px', 
                  textAlign: 'center',
                  border: '2px dashed #dee2e6'
                }}>
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: '#e9ecef',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    <TokenIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No optional claims configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This application does not have any optional claims configured
                  </Typography>
                </Box>
              ) : (
                // Enhanced claims display with better token type separation
                <Box sx={{ p: 3, backgroundColor: "#fafbff" }}>
                  <Stack spacing={3}>
                    {['accessToken', 'idToken', 'saml2Token'].map((tokenType) => {
                      const claims = modifiedApp.optionalClaims?.[tokenType as keyof typeof modifiedApp.optionalClaims] || [];
                      if (!claims.length) return null;
                      
                      const tokenTypeLabels = {
                        accessToken: 'Access Token',
                        idToken: 'ID Token', 
                        saml2Token: 'SAML2 Token'
                      };
                      
                      const tokenTypeColors = {
                        accessToken: '#1976d2',
                        idToken: '#7b1fa2',
                        saml2Token: '#f57c00'
                      };

                      return (
                        <Box key={tokenType}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 2,
                            pb: 1,
                            borderBottom: `2px solid ${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}20`
                          }}>
                            <Box sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '4px',
                              backgroundColor: tokenTypeColors[tokenType as keyof typeof tokenTypeColors],
                              mr: 1.5
                            }} />
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600,
                                color: tokenTypeColors[tokenType as keyof typeof tokenTypeColors],
                                fontSize: '1.1rem'
                              }}
                            >
                              {tokenTypeLabels[tokenType as keyof typeof tokenTypeLabels]} Optional Claims
                            </Typography>
                            <Chip 
                              size="small"
                              label={`${claims.length} claims`}
                              sx={{ 
                                ml: 'auto',
                                backgroundColor: `${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}15`,
                                color: tokenTypeColors[tokenType as keyof typeof tokenTypeColors]
                              }}
                            />
                          </Box>
                          
                          <List sx={{ 
                            bgcolor: "#ffffff", 
                            border: `1px solid ${tokenTypeColors[tokenType as keyof typeof tokenTypeColors]}30`, 
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            {claims.map((claim: any, index: number) => (
                              <ListItem
                                key={`${tokenType}-${index}`}
                                sx={{
                                  borderBottom: index < claims.length - 1 ? "1px solid #f5f5f5" : "none",
                                  '&:hover': { backgroundColor: '#fafafa' }
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="body1" fontWeight="medium" sx={{ mr: 1 }}>
                                        {claim.name}
                                      </Typography>
                                      {claim.essential && (
                                        <Chip 
                                          size="small" 
                                          label="Essential" 
                                          color="warning"
                                          variant="outlined"
                                          sx={{ height: '20px', fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={`Required: ${claim.essential ? "Yes" : "No"}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* 2) INFORMATION ACCORDION - De-emphasized Design with consistent styling */}
              <Box sx={{ 
                m: 2, 
                borderRadius: '8px', // Smaller radius
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)', // Much lighter shadow
                opacity: 0.85 // Slight transparency to de-emphasize
              }}>
                <Accordion sx={{
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important'
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.2rem', color: '#9e9e9e' }} />} // Smaller, grayed icon
                    sx={{
                      background: '#f8f9fa', // Simple gray background instead of gradient
                      borderBottom: '1px solid #e9ecef',
                      minHeight: '48px', // Shorter height
                      '&:hover': {
                        backgroundColor: '#f1f3f4' // Subtle hover effect
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <InfoOutlinedIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: 'info.main' }} /> {/* Updated to match Claims Mapping Policy styling */}
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                          fontSize: "1rem", 
                          fontWeight: 500, 
                          letterSpacing: "0.005em"
                        }}
                      >
                        About Optional Claims
                      </Typography>
                      <Chip 
                        size="small"
                        label="Info" 
                        variant="outlined" 
                        sx={{ 
                          ml: 2,
                          borderColor: '#dee2e6',
                          color: '#6c757d',
                          fontSize: '0.65rem', 
                          height: '20px', 
                          fontWeight: 400
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Optional claims allow you to include additional user attributes in tokens that wouldn't normally be included.
                      </Typography>
                      
                      {/* Visual explanation of claims */}
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e0e0ff', 
                        borderRadius: 1,
                        mb: 2
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                          Understanding Token Types:
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Access Tokens</strong>: Used to access protected resources and APIs. Claims added here are available to API endpoints.<br />
                          • <strong>ID Tokens</strong>: Used for authentication and contain user identity information. Claims here are available to your client application.<br />
                          • <strong>SAML2 Tokens</strong>: Used for legacy applications that use SAML protocol instead of OAuth/OIDC.
                        </Typography>
                      </Box>
                      
                      <Box sx={{
                        p: 2,
                        backgroundColor: '#fff8e1',
                        border: '1px solid #ffe082',
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                          Important Notes:
                        </Typography>
                        <Typography variant="body2">
                          • <strong>Essential claims</strong> are marked as required, but some identity providers may still omit them if unavailable.<br />
                          • Some claims require specific permissions to be configured in the API Permissions section.<br />
                          • Changes to claims require user consent again if users have previously consented to your application.
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>

                </Accordion>
              </Box>

            </Box>          </AccordionDetails>
        </Accordion>
              
      </CardContent>
    </Card>
  );
};

export default OptionalClaims;
